import {
  Button,
  Page,
  Toast,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import styledComponents from "styled-components";

import { useActiveRoom, useAllRooms } from "../hooks/use-rooms";
import { ladsWebSocketService } from "../util/websocket-lads";
import { MenuContent } from "./menu-content";

const StyledToastContainer = styledComponents.div`
  div.roomToast > div {
    margin-left: 40px;
    margin-right: 40px;
  }
`;

export const PagePartial = ({
  children,
  title,
}: {
  title: string;
  children?: ReactNode;
}) => {
  const { t } = useTranslation();
  const { rooms } = useAllRooms();
  const { activeRoom, setActiveRoom } = useActiveRoom();

  // Global WS join: every page that uses this wrapper subscribes to the
  // user's currently-selected room. The gateway's hierarchical fanout then
  // delivers value/metadata/incident events from every device beneath it,
  // even when the page itself doesn't bind to a specific device room
  // (e.g. Workplaces, Dashboard).
  const activeRoomId = activeRoom?.id?.toString();
  useEffect(() => {
    if (!activeRoomId) return;
    ladsWebSocketService.subscribe(activeRoomId);
    return () => {
      ladsWebSocketService.unsubscribe(activeRoomId);
    };
  }, [activeRoomId]);

  const [searchParams] = useSearchParams();
  const envRoomId = import.meta.env.VITE_ROOM_ID;
  const defaultRoomId = searchParams.get("defaultRoom") || envRoomId;
  const defaultRoom = rooms?.find(
    (room) => room.id.toString() === defaultRoomId
  );
  const navigate = useNavigate();

  const onBackDefaultRoom = () => {
    setActiveRoom(defaultRoom);
    navigate({
      pathname: "/",
      search: defaultRoomId ? `defaultRoom=${defaultRoomId}` : "",
    });
  };

  return (
    <StyledToastContainer>
      <Toast
        className="roomToast"
        hidden={!(defaultRoomId && activeRoom?.id.toString() !== defaultRoomId)}
      >
        <Typography>
          {t("room.attentiontext")} {activeRoom?.name}
        </Typography>{" "}
        <Button onClick={() => onBackDefaultRoom()}>{t("room.home")}</Button>
      </Toast>
      <Page menu={<MenuContent />} title={title}>
        {children}
      </Page>
    </StyledToastContainer>
  );
};
